import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Wallet, Laptop, Translate, CaretDown, CaretUp, Funnel } from '@phosphor-icons/react';
import type { LocationTrendsData } from '../hooks/useLocationTrends';
import { usePageContext } from 'vike-react/usePageContext';
import { useSearchFilters, useSearchQuery } from '../contexts/SearchContext';
import { usePushIndexUiState } from '../hooks/usePushIndexUiState';
import type { IndexUiState } from '../utils/algolia/url-handling';
import { BASE_URL } from '../utils/constants/url-constants';
import { FINNISH_LOCALITY_LOCALE_NAMES } from '../utils/geocoding/finnish-locality-locale-names';
import { Link } from './Link';
import { 
  getLocationBackgroundImageSrcset, 
  getLocationBackgroundImageDefaultSrc,
  formatLocationToKebabCase 
} from '../utils/location/get-location-background-image-url';
import { generateLocationMarketHeaderText } from '../utils/location/generate-location-market-header-text';
import { formatLocationBackgroundImageAltFromIntl } from '../utils/location/format-location-background-image-alt';
import { Tooltip } from './Tooltip';
import { detectBackgroundBrightness } from '../utils/image/detect-background-brightness';
import { useThrottledResize } from '../hooks/useThrottledResize';
import { FILTER_CAT_JOB, FILTER_CAT_LOCATION, FILTER_CAT_SALARY } from '../constants/corePublicTheme';
import { buildLocationSalaryPercentileSeoData } from '../utils/location/build-location-salary-percentile-seo';

/**
 * Locations that have specific background images (normalized to English format)
 * These match the locations processed by the processCompanyLogos lambda
 */
const LOCATIONS_WITH_BACKGROUNDS = new Set([
  'Helsinki, Finland',
  'Tampere, Finland',
  'Turku, Finland',
  'Oulu, Finland',
  'Jyväskylä, Finland',
  'Lahti, Finland',
  'Kuopio, Finland',
  'Pori, Finland',
  'Joensuu, Finland',
  'Vaasa, Finland',
  'Lappeenranta, Finland',
  'Rovaniemi, Finland',
  'Seinäjoki, Finland',
  'Hämeenlinna, Finland',
  'Kotka, Finland',
  'Kouvola, Finland',
  'Porvoo, Finland',
  'Kokkola, Finland',
  'Mikkeli, Finland',
  'Rauma, Finland',
  'Espoo, Finland',
  'Hanko, Finland',
  'Vantaa, Finland',
  'Kemi, Finland',
]);

/**
 * Generic Finland background image: use an existing city image we already fetch and convert (Lambda).
 * No separate "Finland (generic)" download - reuse Rovaniemi's image as fallback.
 */
const GENERIC_FINLAND_LOCATION = 'Rovaniemi, Finland';

/** Same locality table as URL routing and job processor (see finnish-locality-locale-names.ts). */
const CITY_NAME_MAPPINGS = FINNISH_LOCALITY_LOCALE_NAMES;

/**
 * Country name mappings
 */
const COUNTRY_NAME_MAPPINGS: Record<string, { fi: string; sv: string; en: string }> = {
  'Finland': { fi: 'Suomi', sv: 'Finland', en: 'Finland' },
  'Suomi': { fi: 'Suomi', sv: 'Finland', en: 'Finland' },
};

/**
 * Normalize location name to English format for background image lookup
 * Handles all language variants (en, fi, sv) and country names (Finland, Suomi)
 */
function normalizeLocationToEnglish(location: string): string {
  if (!location) return location;

  // Try to match directly first
  if (LOCATIONS_WITH_BACKGROUNDS.has(location)) {
    return location;
  }

  // Parse location (format: "City, Country" or "City_Country")
  const parts = location.includes(',') 
    ? location.split(',').map(p => p.trim())
    : location.split('_').map(p => p.trim());

  if (parts.length < 2) {
    // Try to find by city name only
    const cityName = parts[0].trim();
    for (const [englishCity, variants] of Object.entries(CITY_NAME_MAPPINGS)) {
      if (
        cityName.toLowerCase() === variants.en.toLowerCase() ||
        cityName.toLowerCase() === variants.fi.toLowerCase() ||
        cityName.toLowerCase() === variants.sv.toLowerCase()
      ) {
        const normalized = `${variants.en}, Finland`;
        return LOCATIONS_WITH_BACKGROUNDS.has(normalized) ? normalized : location;
      }
    }
    return location;
  }

  const cityPart = parts[0].trim();
  const countryPart = parts[1].trim();

  // Find English city name (case-insensitive matching)
  let englishCity = cityPart;
  for (const [englishCityName, variants] of Object.entries(CITY_NAME_MAPPINGS)) {
    if (
      cityPart.toLowerCase() === variants.en.toLowerCase() ||
      cityPart.toLowerCase() === variants.fi.toLowerCase() ||
      cityPart.toLowerCase() === variants.sv.toLowerCase()
    ) {
      englishCity = englishCityName;
      break;
    }
  }

  // Normalize country to "Finland"
  const englishCountry = 'Finland';

  const normalized = `${englishCity}, ${englishCountry}`;
  
  // Return normalized location if we have a background image, otherwise return original
  return LOCATIONS_WITH_BACKGROUNDS.has(normalized) ? normalized : location;
}

/**
 * Get background image information for a location
 * Returns an object with srcset and default src for responsive images
 * Falls back to generic Finland image if no specific mapping exists
 */
function getLocationBackgroundImage(location: string): { srcset: string; src: string } | undefined {
  const normalized = normalizeLocationToEnglish(location);
  
  // Check if we have a specific image for this location
  if (LOCATIONS_WITH_BACKGROUNDS.has(normalized)) {
    return {
      srcset: getLocationBackgroundImageSrcset(normalized),
      src: getLocationBackgroundImageDefaultSrc(normalized),
    };
  }
  
  // If location is in Finland but no specific image, use generic Finland image
  if (normalized.includes('Finland') || normalized.includes('Suomi')) {
    return {
      srcset: getLocationBackgroundImageSrcset(GENERIC_FINLAND_LOCATION),
      src: getLocationBackgroundImageDefaultSrc(GENERIC_FINLAND_LOCATION),
    };
  }
  
  return undefined;
}

export interface MarketHeaderProps {
  location: string;
  nbHits: number;
  description?: string;
  backgroundImage?: string; // Optional override, otherwise auto-detected from location
  locationTrendsData?: LocationTrendsData | null;
  className?: string;
}


/**
 * Segmented Progress Bar for work model distribution
 */
const WorkModelBar: React.FC<{
  onsite: number;
  hybrid: number;
  remote: number;
  intl: any;
}> = ({ onsite, hybrid, remote, intl }) => {
  const total = onsite + hybrid + remote;
  if (total === 0) return null;

  const onsitePercent = (onsite / total) * 100;
  const hybridPercent = (hybrid / total) * 100;
  const remotePercent = (remote / total) * 100;

  return (
    <div className="w-full h-3 bg-[#DDD5C8] dark:bg-[#4A3520] rounded-full overflow-hidden flex">
      {onsitePercent > 0 && (
        <div
          className="bg-[#3B5E45] dark:bg-[#5A8A68]"
          style={{ width: `${onsitePercent}%` }}
          title={intl.formatMessage(
            { id: 'marketHeader.workModel.onsitePercent', defaultMessage: '{percent}% Onsite', description: 'Tooltip showing percentage of onsite work positions, where {percent} is the number' },
            { percent: Math.round(onsitePercent) }
          )}
        />
      )}
      {hybridPercent > 0 && (
        <div
          className="bg-[#5A8A68] dark:bg-[#98CFA8]"
          style={{ width: `${hybridPercent}%` }}
          title={intl.formatMessage(
            { id: 'marketHeader.workModel.hybridPercent', defaultMessage: '{percent}% Hybrid', description: 'Tooltip showing percentage of hybrid work positions, where {percent} is the number' },
            { percent: Math.round(hybridPercent) }
          )}
        />
      )}
      {remotePercent > 0 && (
        <div
          className="bg-[#90BC9C] dark:bg-[#B6D4BC]"
          style={{ width: `${remotePercent}%` }}
          title={intl.formatMessage(
            { id: 'marketHeader.workModel.remotePercent', defaultMessage: '{percent}% Remote', description: 'Tooltip showing percentage of remote work positions, where {percent} is the number' },
            { percent: Math.round(remotePercent) }
          )}
        />
      )}
    </div>
  );
};

/**
 * Segmented Progress Bar for language distribution
 */
const LanguageBar: React.FC<{
  english: number;
  local: number;
}> = ({ english, local }) => {
  const total = english + local;
  if (total === 0) return null;

  const englishPercent = (english / total) * 100;
  const localPercent = (local / total) * 100;

  return (
    <div className="w-full h-3 bg-[#DDD5C8] dark:bg-[#4A3520] rounded-full overflow-hidden flex">
      {englishPercent > 0 && (
        <div
          className="bg-[#3B5E45] dark:bg-[#98CFA8]"
          style={{ width: `${englishPercent}%` }}
          title={`${Math.round(englishPercent)}% English`}
        />
      )}
      {localPercent > 0 && (
        <div
          className="bg-[#C8DDCE] dark:bg-[#1A2E22]"
          style={{ width: `${localPercent}%` }}
          title={`${Math.round(localPercent)}% Local`}
        />
      )}
    </div>
  );
};

/**
 * MarketHeader - Apple-style location header component
 */
export const MarketHeader: React.FC<MarketHeaderProps> = ({
  location,
  nbHits,
  description,
  backgroundImage,
  locationTrendsData,
  className = '',
}) => {
  const intl = useIntl();
  const { filters } = useSearchFilters();
  const { query } = useSearchQuery();
  const { pushIndexUiState } = usePushIndexUiState();
  const pageContext = usePageContext();
  // Client-side state for manual toggle (overrides Tailwind defaults)
  const [isCollapsed, setIsCollapsed] = React.useState<boolean | null>(null);
  const [useWhiteText, setUseWhiteText] = React.useState(true); // Default to white text (dark background)
  const [isLargeScreen, setIsLargeScreen] = React.useState(false); // For conditional tooltip rendering
  const [backgroundImageLoadFailed, setBackgroundImageLoadFailed] = React.useState(false);
  const imageRef = React.useRef<HTMLImageElement | null>(null);

  // Check if screen is lg or larger (1024px+) for tooltip rendering
  // Use throttled resize to prevent lag
  const checkScreenSize = React.useCallback(() => {
    setIsLargeScreen(typeof window !== 'undefined' && window.innerWidth >= 1024);
  }, []);
  
  React.useEffect(() => {
    // Check on mount
    checkScreenSize();
  }, [checkScreenSize]);
  
  // Use throttled resize hook to prevent lag
  useThrottledResize(checkScreenSize);

  // Parse filters
  const parsedFilters = React.useMemo(() => {
    if (!filters) return {};
    if (typeof filters === 'string') {
      try {
        return JSON.parse(filters) as Record<string, string[]>;
      } catch {
        return {};
      }
    }
    return filters as Record<string, string[]>;
  }, [filters]);

  // Extract data from locationTrendsData (available from SSR)
  const salaryStats = locationTrendsData?.salaryStats?.aggregate;
  const remotenessDistribution = locationTrendsData?.trends?.remotenessDistribution || {};
  const languageRequirements = locationTrendsData?.trends?.languageRequirements || {};
  
  // Calculate sample count for samples button (must work server-side)
  // Priority: salaryStats.sampleSize > companyDistribution count > nbHits (as fallback)
  // This ensures the samples button renders server-side when locationTrendsData is available from SSR
  // Uses source data directly to avoid dependency issues during SSR
  const sampleCount = React.useMemo(() => {
    // Check salaryStats.sampleSize first (most accurate)
    const salarySampleSize = locationTrendsData?.salaryStats?.aggregate?.sampleSize;
    if (salarySampleSize && salarySampleSize > 0) return salarySampleSize;
    
    // Check companyJobCount (total jobs across all companies, pre-computed before capping)
    const companyJobCount = locationTrendsData?.trends?.companyJobCount;
    if (companyJobCount && companyJobCount > 0) return companyJobCount;
    // Fallback: sum capped companyDistribution entries (approximate)
    if (locationTrendsData?.trends?.companyDistribution) {
      const count = Object.values(locationTrendsData.trends.companyDistribution).reduce(
        (sum, count) => sum + (typeof count === 'number' ? count : 0),
        0
      );
      if (count > 0) return count;
    }
    
    // Use nbHits as final fallback (always available from search results)
    return nbHits || 0;
  }, [
    locationTrendsData?.salaryStats?.aggregate?.sampleSize,
    locationTrendsData?.trends?.companyDistribution,
    nbHits,
  ]);

  // Calculate salary range from stats
  const salaryRange = React.useMemo(() => {
    if (!salaryStats) return null;
    const min = salaryStats.min || 0;
    const max = salaryStats.max || 0;
    const percentile25 = salaryStats.percentile25?.min || 0;
    const percentile75 = salaryStats.percentile75?.min || 0;
    
    if (min === 0 && max === 0) return null;
    
    // Show 25th-75th percentile range if available, otherwise min-max
    if (percentile25 > 0 && percentile75 > 0) {
      return {
        low: percentile25,
        high: percentile75,
        isPercentile: true,
      };
    }
    
    return {
      low: min,
      high: max,
      isPercentile: false,
    };
  }, [salaryStats]);
  const salaryPercentileSeoData = React.useMemo(
    () => buildLocationSalaryPercentileSeoData(salaryStats),
    [salaryStats],
  );

  // Calculate work model percentages and determine headline
  const workModelData = React.useMemo(() => {
    const onsite = remotenessDistribution.onsite || 0;
    const hybrid = remotenessDistribution.hybrid || 0;
    const remote = remotenessDistribution.remote || 0;
    const total = onsite + hybrid + remote;
    
    if (total === 0) {
      return { onsite, hybrid, remote, total, headline: null, breakdown: null, breakdownData: null };
    }

    const onsitePercent = (onsite / total) * 100;
    const hybridPercent = (hybrid / total) * 100;
    const remotePercent = (remote / total) * 100;
    const flexiblePercent = hybridPercent + remotePercent;

    // Determine headline based on distribution
    let headlineKey: string;
    let headlineDefault: string;
    if (onsitePercent > 60) {
      headlineKey = 'marketHeader.workModel.primarilyOnsite';
      headlineDefault = 'Primarily Onsite';
    } else if (hybridPercent > 40 || (hybridPercent > onsitePercent && hybridPercent > remotePercent)) {
      headlineKey = 'marketHeader.workModel.hybridPopular';
      headlineDefault = 'Hybrid Popular';
    } else if (remotePercent > 30 || (remotePercent > onsitePercent && remotePercent > hybridPercent)) {
      headlineKey = 'marketHeader.workModel.remoteFriendly';
      headlineDefault = 'Remote-Friendly';
    } else if (flexiblePercent > 60) {
      headlineKey = 'marketHeader.workModel.flexibleWork';
      headlineDefault = 'Flexible Work';
    } else if (onsitePercent > hybridPercent && onsitePercent > remotePercent) {
      headlineKey = 'marketHeader.workModel.primarilyOnsite';
      headlineDefault = 'Primarily Onsite';
    } else if (hybridPercent > remotePercent) {
      headlineKey = 'marketHeader.workModel.hybridPopular';
      headlineDefault = 'Hybrid Popular';
    } else {
      headlineKey = 'marketHeader.workModel.remoteFriendly';
      headlineDefault = 'Remote-Friendly';
    }

    // Create breakdown text - will be translated in the component
    const breakdownData = {
      onsitePercent: onsitePercent > 0 ? Math.round(onsitePercent) : null,
      hybridPercent: hybridPercent > 0 ? Math.round(hybridPercent) : null,
      remotePercent: remotePercent > 0 ? Math.round(remotePercent) : null,
    };
    const breakdown = breakdownData;

    return { 
      onsite, 
      hybrid, 
      remote, 
      total, 
      headline: headlineKey,
      headlineDefault,
      breakdown: breakdownData,
      breakdownData,
      onsitePercent,
      hybridPercent,
      remotePercent,
    };
  }, [remotenessDistribution]);

  // Calculate language distribution (English vs Local)
  const languageData = React.useMemo(() => {
    const english = languageRequirements['English'] || 0;
    const finnish = languageRequirements['Finnish'] || languageRequirements['Suomi'] || 0;
    const swedish = languageRequirements['Swedish'] || languageRequirements['Svenska'] || 0;
    const local = finnish + swedish;
    return { english, local, total: english + local };
  }, [languageRequirements]);

  // Format salary value
  const salaryValue = React.useMemo(() => {
    if (!salaryStats) return null;
    const avg = salaryStats.average?.min || salaryStats.median?.min || 0;
    if (avg === 0) return null;
    return `€${Math.round(avg / 1000)}k/mo`;
  }, [salaryStats]);

  // Get background image (use prop if provided, otherwise auto-detect from location)
  const effectiveBackgroundImage = React.useMemo(() => {
    // If a backgroundImage prop is provided as a string (for backward compatibility), use it
    if (backgroundImage && typeof backgroundImage === 'string') {
      return { src: backgroundImage, srcset: '' };
    }
    
    // Otherwise, auto-detect from location using the generated S3 images
    return getLocationBackgroundImage(location);
  }, [backgroundImage, location]);

  // Reset load-failed state when the effective image source changes (e.g. location change)
  React.useEffect(() => {
    setBackgroundImageLoadFailed(false);
  }, [effectiveBackgroundImage?.src]);

  const isShowingBackgroundImage = Boolean(effectiveBackgroundImage && !backgroundImageLoadFailed);

  // Handle removing location filter
  const handleRemoveLocation = React.useCallback(() => {
    const { location: _, ...remainingFilters } = parsedFilters;
    const nextState: IndexUiState = {
      refinementList: remainingFilters,
      page: 0,
    };
    if (typeof query === 'string' && query.trim().length > 0) {
      nextState.query = query;
    }
    pushIndexUiState(nextState);
  }, [parsedFilters, pushIndexUiState, query]);

  // Build filter state for pill buttons
  const buildFilterState = React.useCallback(
    (attribute: string, value: string): IndexUiState => {
      const baseRefinements = Object.entries(parsedFilters).reduce<Record<string, string[]>>(
        (acc, [key, val]) => {
          if (Array.isArray(val) && val.length > 0) {
            acc[key] = [...val];
          }
          return acc;
        },
        {}
      );

      const trimmedValue = value.trim();
      if (trimmedValue.length > 0) {
        const existing = baseRefinements[attribute] ? [...baseRefinements[attribute]] : [];
        if (!existing.includes(trimmedValue)) {
          existing.push(trimmedValue);
        }
        baseRefinements[attribute] = existing;
      }

      const nextState: IndexUiState = {
        refinementList: baseRefinements,
        page: 0,
      };

      if (typeof query === 'string' && query.trim().length > 0) {
        nextState.query = query;
      }

      return nextState;
    },
    [parsedFilters, query]
  );

  const hasEnglishFilter = parsedFilters.requiredLanguages?.includes('English');
  const hasRemoteFilter = parsedFilters.remoteness?.some((r) =>
    r.toLowerCase().includes('remote')
  );
  const hasHighSalaryFilter = parsedFilters.salaryRangeMin && parsedFilters.salaryRangeMin.length > 0;

  // Calculate data for SEO
  const remotePercentage = workModelData.total > 0 
    ? Math.round(((workModelData.remote + workModelData.hybrid) / workModelData.total) * 100)
    : 0;
  const avgSalaryNum = salaryStats?.average?.min || salaryStats?.median?.min || 0;
  const avgSalaryFormatted = avgSalaryNum > 0 ? `€${Math.round(avgSalaryNum / 1000)}k` : 'N/A';
  const companyDistributionEntries = locationTrendsData?.trends?.companyDistribution
    ? Object.entries(locationTrendsData.trends.companyDistribution)
    : [];
  const topCompanies = companyDistributionEntries
    .sort(([, a], [, b]) => (b as number) - (a as number))
    .slice(0, 3)
    .map(([name]) => name);
  const topCompany = topCompanies[0] ?? null;

  // Extract top industries from fieldDistribution
  const fieldDistributionEntries = locationTrendsData?.trends?.fieldDistribution
    ? Object.entries(locationTrendsData.trends.fieldDistribution)
    : [];
  const topIndustries = fieldDistributionEntries
    .sort(([, a], [, b]) => (b as number) - (a as number))
    .slice(0, 3)
    .map(([name]) => name);
  // Only use lastUpdated if it comes from stable data (salaryStats)
  // Never use Date.now() or new Date() here to avoid hydration mismatches
  const lastUpdated = salaryStats?.lastUpdated || null;
  const lastUpdatedDate = lastUpdated ? new Date(lastUpdated) : null;
  const locale = intl.locale as 'en' | 'fi' | 'sv';

  // Translate location name based on locale
  const translatedLocation = React.useMemo(() => {
    if (!location) return location;
    
    // Parse location (format: "City, Country" or "City_Country")
    const parts = location.includes(',') 
      ? location.split(',').map(p => p.trim())
      : location.split('_').map(p => p.trim());
    
    if (parts.length < 2) {
      // Just city name, try to translate it
      const cityName = parts[0].trim();
      for (const [englishCity, variants] of Object.entries(CITY_NAME_MAPPINGS)) {
        if (
          cityName.toLowerCase() === variants.en.toLowerCase() ||
          cityName.toLowerCase() === variants.fi.toLowerCase() ||
          cityName.toLowerCase() === variants.sv.toLowerCase()
        ) {
          return variants[locale];
        }
      }
      return cityName;
    }
    
    const cityPart = parts[0].trim();
    const countryPart = parts[1].trim();
    
    // Translate city name
    let translatedCity = cityPart;
    for (const [englishCity, variants] of Object.entries(CITY_NAME_MAPPINGS)) {
      if (
        cityPart.toLowerCase() === variants.en.toLowerCase() ||
        cityPart.toLowerCase() === variants.fi.toLowerCase() ||
        cityPart.toLowerCase() === variants.sv.toLowerCase()
      ) {
        translatedCity = variants[locale];
        break;
      }
    }
    
    // Translate country name
    let translatedCountry = countryPart;
    for (const [countryKey, variants] of Object.entries(COUNTRY_NAME_MAPPINGS)) {
      if (
        countryPart.toLowerCase() === variants.en.toLowerCase() ||
        countryPart.toLowerCase() === variants.fi.toLowerCase() ||
        countryPart.toLowerCase() === variants.sv.toLowerCase()
      ) {
        translatedCountry = variants[locale];
        break;
      }
    }
    
    return `${translatedCity}, ${translatedCountry}`;
  }, [location, locale]);

  const backgroundImageAlt = React.useMemo(
    () => formatLocationBackgroundImageAltFromIntl(intl, translatedLocation),
    [intl, translatedLocation]
  );

  // Extract Finland averages from locationTrendsData (if available from API)
  const finlandAverages = React.useMemo(() => {
    // First try to get from locationTrendsData (from API)
    const finlandDataFromTrends = locationTrendsData?.finlandAverages;
    if (finlandDataFromTrends) {
      return {
        avgSalaryNum: finlandDataFromTrends.avgSalaryNum,
        avgSalaryFormatted: finlandDataFromTrends.avgSalaryFormatted,
        remotePercentage: finlandDataFromTrends.remotePercentage,
        englishLanguagePercentage: finlandDataFromTrends.englishLanguagePercentage,
      };
    }
    
    // Fallback to pageContext data
    const finlandData = (pageContext as any)?.data?.finlandAverages;
    if (!finlandData) return undefined;
    
    return {
      avgSalaryNum: finlandData.avgSalaryNum,
      avgSalaryFormatted: finlandData.avgSalaryFormatted,
      remotePercentage: finlandData.remotePercentage,
      englishLanguagePercentage: finlandData.englishLanguagePercentage,
    };
  }, [pageContext, locationTrendsData]);

  // Generate rich textual blurbs for crawlers and users
  const locationHeaderText = React.useMemo(() => {
    return generateLocationMarketHeaderText({
      location,
      locale,
      nbHits,
      sampleCount,
      remotePercentage,
      avgSalaryNum,
      avgSalaryFormatted,
      languageTotals: {
        english: languageData.english,
        local: languageData.local,
        total: languageData.total,
      },
      topCompanies,
      topIndustries,
      finlandAverages,
    });
  }, [
    location,
    locale,
    nbHits,
    sampleCount,
    remotePercentage,
    avgSalaryNum,
    avgSalaryFormatted,
    languageData.english,
    languageData.local,
    languageData.total,
    topCompanies,
    topIndustries,
    finlandAverages,
  ]);

  // Generate Q&A structure for AI Overviews
  const qaContent = React.useMemo(() => {
    const remoteText = remotePercentage > 0 
      ? `${remotePercentage}% of roles are remote or hybrid`
      : 'Remote work options are available';
    const salaryText = avgSalaryNum > 0
      ? `Average salary is ${avgSalaryFormatted}/month`
      : 'Salary data available';
    return {
      remote: `Is ${translatedLocation} remote-friendly? Yes, ${remoteText}.`,
      salary: `What is the average salary in ${translatedLocation}? ${salaryText}.`,
      language: languageData.total > 0
        ? `What languages are required in ${translatedLocation}? ${Math.round((languageData.english / languageData.total) * 100)}% of jobs require English.`
        : `What languages are required in ${translatedLocation}? Language requirements vary by role.`,
    };
  }, [translatedLocation, remotePercentage, avgSalaryFormatted, avgSalaryNum, languageData]);

  // Generate nearby locations (major Finnish cities)
  const nearbyLocations = React.useMemo(() => {
    const majorCities = [
      { name: 'Helsinki', slug: 'Helsinki_Finland', displayName: locale === 'sv' ? 'Helsingfors' : 'Helsinki' },
      { name: 'Tampere', slug: 'Tampere_Finland', displayName: locale === 'sv' ? 'Tammerfors' : 'Tampere' },
      { name: 'Turku', slug: 'Turku_Finland', displayName: locale === 'sv' ? 'Åbo' : 'Turku' },
      { name: 'Oulu', slug: 'Oulu_Finland', displayName: locale === 'sv' ? 'Uleåborg' : 'Oulu' },
      { name: 'Espoo', slug: 'Espoo_Finland', displayName: locale === 'sv' ? 'Esbo' : 'Espoo' },
      { name: 'Vantaa', slug: 'Vantaa_Finland', displayName: locale === 'sv' ? 'Vanda' : 'Vantaa' },
    ];
    // Filter out current location (check both name and display name)
    // Use translatedLocation to get the localized city name for comparison
    const currentCity = translatedLocation ? translatedLocation.split(',')[0].trim() : location.split(',')[0].trim();
    return majorCities
      .filter(city => city.name !== currentCity && city.displayName !== currentCity)
      .slice(0, 6);
  }, [location, translatedLocation, locale]);

  // Generate FAQ questions for structured data
  const faqQuestions = React.useMemo(() => {
    const questions = [];
    if (avgSalaryNum > 0) {
      questions.push({
        question: `What is the average salary in ${translatedLocation}?`,
        answer: `The average salary in ${translatedLocation} is ${avgSalaryFormatted}/month based on ${salaryStats?.sampleSize || 0} job postings.`,
      });
    }
    if (remotePercentage > 0) {
      questions.push({
        question: `Is ${translatedLocation} remote-friendly?`,
        answer: `Yes, ${remotePercentage}% of job postings in ${translatedLocation} offer remote or hybrid work arrangements.`,
      });
    }
    if (languageData.total > 0) {
      const englishPercent = Math.round((languageData.english / languageData.total) * 100);
      questions.push({
        question: `What languages are required for jobs in ${translatedLocation}?`,
        answer: `${englishPercent}% of job postings in ${translatedLocation} require English, with ${100 - englishPercent}% requiring Finnish, Swedish, or other languages.`,
      });
    }
    return questions;
  }, [translatedLocation, avgSalaryNum, avgSalaryFormatted, salaryStats, remotePercentage, languageData]);

  // Generate structured data (JSON-LD)
  const structuredData = React.useMemo(() => {
    const locationSegment = locale === 'fi' ? 'alue' : locale === 'sv' ? 'omrade' : 'location';
    const jobsSegment = locale === 'en' ? 'jobs' : locale === 'fi' ? 'tyopaikat' : 'lediga-jobb';
    const locationSlug = location.replace(/, /g, '_').replace(/ /g, '_');
    const datasetUrl = `${BASE_URL}/${locale}/${jobsSegment}/${locationSegment}/${locationSlug}`;

    // Generate significant links to nearby locations
    const significantLinks = nearbyLocations.map(city => {
      const cityLocationSegment = locale === 'fi' ? 'alue' : locale === 'sv' ? 'omrade' : 'location';
      const cityJobsSegment = locale === 'en' ? 'jobs' : locale === 'fi' ? 'tyopaikat' : 'lediga-jobb';
      return `${BASE_URL}/${locale}/${cityJobsSegment}/${cityLocationSegment}/${city.slug}`;
    });

    const dataset = {
      '@context': 'https://schema.org',
      '@type': 'Dataset',
      name: `2026 Job Market Analytics for ${translatedLocation}`,
      description: `Live data on salary trends, language requirements, and work-model distribution for ${translatedLocation}.`,
      url: datasetUrl,
      ...(lastUpdatedDate ? {
        datePublished: lastUpdatedDate.toISOString(),
        dateModified: lastUpdatedDate.toISOString(),
      } : {}),
      spatialCoverage: {
        '@type': 'Place',
        name: translatedLocation,
        address: {
          '@type': 'PostalAddress',
          addressLocality: translatedLocation.split(',')[0].trim(),
          addressCountry: location.includes('Finland') || location.includes('Suomi') || translatedLocation.includes('Finland') ? 'FI' : undefined,
        },
      },
      variableMeasured: [
        {
          '@type': 'PropertyValue',
          name: 'Average Salary',
          value: avgSalaryFormatted,
        },
        {
          '@type': 'PropertyValue',
          name: 'Remote Percentage',
          value: `${remotePercentage}%`,
        },
        ...(topCompany ? [{
          '@type': 'PropertyValue',
          name: 'Top Hiring Company',
          value: topCompany,
        }] : []),
        ...(languageData.total > 0 ? [{
          '@type': 'PropertyValue',
          name: 'English Language Requirement Percentage',
          value: `${Math.round((languageData.english / languageData.total) * 100)}%`,
        }] : []),
      ],
      ...(salaryStats?.sampleSize ? {
        numberOfItems: salaryStats.sampleSize,
      } : {}),
      ...(significantLinks.length > 0 ? {
        significantLink: significantLinks,
      } : {}),
    };

    const faqPage = faqQuestions.length > 0 ? {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqQuestions.map(q => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: q.answer,
        },
      })),
    } : null;

    return { dataset, faqPage };
  }, [location, translatedLocation, locale, lastUpdatedDate, avgSalaryFormatted, remotePercentage, topCompany, salaryStats, faqQuestions, nearbyLocations, languageData]);

  return (
    <>
      {/* Structured Data (JSON-LD) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData.dataset) }}
      />
      {structuredData.faqPage && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData.faqPage) }}
        />
      )}

      <section id="location-info-header" className={`location-info-header market-header relative overflow-hidden ${className}`} aria-labelledby="location-header-title">
      {/* Background - show gradient if no image, or if image failed to load (e.g. fallback S3 image missing) */}
      {isShowingBackgroundImage ? (
        <div className="absolute inset-0 rounded-b-2xl sm:rounded-b-3xl overflow-hidden">
          <img
            ref={imageRef}
            src={effectiveBackgroundImage.src}
            srcSet={effectiveBackgroundImage.srcset || undefined}
            sizes="100vw"
            alt={backgroundImageAlt}
            className="w-full h-full object-cover"
            loading="lazy"
            onLoad={() => {
              if (imageRef.current) {
                const brightness = detectBackgroundBrightness(imageRef.current);
                setUseWhiteText(brightness.useWhiteText);
              }
            }}
            onError={() => {
              setBackgroundImageLoadFailed(true);
            }}
          />
          <div className="absolute inset-0 bg-black/30" />
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#F8F5F0] via-[#F2EADF] to-[#C8DDCE]/50 dark:from-[#170F07] dark:via-[#2A1D0F] dark:to-[#1A2E22] rounded-b-2xl sm:rounded-b-3xl" />
      )}

      {/* Content */}
      <div className="relative z-10 p-6 sm:p-8 lg:p-12">
        {/* Header with close button */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex-1">
            <div className="flex items-baseline gap-4 mb-3">
              <h1 id="location-header-title" className={`text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight ${
                isShowingBackgroundImage 
                  ? (useWhiteText ? 'text-white' : 'text-gray-900')
                  : 'text-[#1E1A14] dark:text-[#F2EADF]'
              }`}>
                <FormattedMessage
                  id="marketHeader.title"
                  defaultMessage="Open jobs in {location}"
                  values={{ location: translatedLocation }}
                  description="Main heading for location market header in a specific location"
                />
              </h1>
            </div>
            <p
              className={`text-sm sm:text-base mt-2 ${
                isShowingBackgroundImage
                  ? useWhiteText
                    ? 'text-white/80'
                    : 'text-gray-600'
                  : 'text-[#3D3529] dark:text-[#C4A882]'
              }`}
            >
              <FormattedMessage
                id="marketHeader.searchResultCount"
                defaultMessage="{count, plural, one {# search result} other {# search results}}"
                values={{ count: nbHits }}
                description="Number of matching search results under the location market header title; # is the locale-formatted count"
              />
            </p>
            {/* Collapsed subtitle - shown when collapsed, hidden on lg+ when using Tailwind defaults */}
            <div className={`text-sm sm:text-base mt-2 ${
              isCollapsed === null
                ? 'lg:hidden' // SSR default: show on md/sm (collapsed), hide on lg+ (expanded)
                : isCollapsed
                  ? '' // Manually collapsed: show
                  : 'hidden' // Manually expanded: hide
            } ${
              isShowingBackgroundImage 
                ? (useWhiteText ? 'text-white/80' : 'text-gray-600')
                : 'text-[#3D3529] dark:text-[#C4A882]'
            }`}>
              <FormattedMessage
                id="marketHeader.collapsed.subtitle"
                defaultMessage="Click to expand and see market details about {location}"
                values={{ location: translatedLocation }}
                description="Subtitle text shown when market header is collapsed, prompting user to expand for more details"
              />
            </div>
          </div>
          {isLargeScreen ? (
            <Tooltip
              content={intl.formatMessage({
                id: 'tooltip.toggleHeader',
                defaultMessage: 'Toggle market header',
                description: 'Tooltip text for button that shows/hides market statistics header section'
              })}
              position="left"
            >
              <button
                onClick={() => {
                  // Toggle: if null (using Tailwind defaults), determine based on viewport
                  // On md/sm: defaults to collapsed, so expand it
                  // On lg+: defaults to expanded, so collapse it
                  if (isCollapsed === null) {
                    const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
                    setIsCollapsed(!isMobile); // Mobile defaults to collapsed, so expand (false). Desktop defaults to expanded, so collapse (true)
                  } else {
                    setIsCollapsed(!isCollapsed);
                  }
                }}
                className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 md:w-12 md:h-12 lg:w-10 lg:h-10 rounded-full flex items-center justify-center transition-colors group cursor-pointer ${
                  isShowingBackgroundImage
                    ? 'bg-white/50 dark:bg-[#2A1D0F]/60 hover:bg-white/60 dark:hover:bg-[#342415]'
                    : 'bg-white/80 dark:bg-[#2A1D0F]/90 hover:bg-white dark:hover:bg-[#342415]'
                }`}
                aria-label={intl.formatMessage({
                  id: 'marketHeader.toggle',
                  defaultMessage: 'Toggle market header',
                  description: 'Button aria-label to show/hide market statistics header section'
                })}
              >
                {/* Show chevron: down when expanded (content visible), up when collapsed (content hidden) */}
                {/* When null: collapsed on md/sm (up), expanded on lg+ (down) */}
                {isCollapsed === null ? (
                  <>
                    {/* Mobile/Tablet: collapsed by default, show up chevron */}
                    <CaretUp size={20} className="sm:w-5 sm:h-5 md:w-5 md:h-5 lg:hidden text-[#3D3529] dark:text-[#C4A882] group-hover:text-[#1E1A14] dark:group-hover:text-[#F2EADF]" />
                    {/* Desktop: expanded by default, show down chevron */}
                    <CaretDown size={18} className="hidden lg:block text-[#3D3529] dark:text-[#C4A882] group-hover:text-[#1E1A14] dark:group-hover:text-[#F2EADF]" />
                  </>
                ) : isCollapsed ? (
                  <CaretUp size={20} className="sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-[18px] lg:h-[18px] text-[#3D3529] dark:text-[#C4A882] group-hover:text-[#1E1A14] dark:group-hover:text-[#F2EADF]" />
                ) : (
                  <CaretDown size={20} className="sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-[18px] lg:h-[18px] text-[#3D3529] dark:text-[#C4A882] group-hover:text-[#1E1A14] dark:group-hover:text-[#F2EADF]" />
                )}
              </button>
            </Tooltip>
          ) : (
            <button
              onClick={() => {
                // Toggle: if null (using Tailwind defaults), determine based on viewport
                // On md/sm: defaults to collapsed, so expand it
                // On lg+: defaults to expanded, so collapse it
                if (isCollapsed === null) {
                  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
                  setIsCollapsed(!isMobile); // Mobile defaults to collapsed, so expand (false). Desktop defaults to expanded, so collapse (true)
                } else {
                  setIsCollapsed(!isCollapsed);
                }
              }}
              className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 md:w-12 md:h-12 lg:w-10 lg:h-10 rounded-full flex items-center justify-center transition-colors group cursor-pointer ${
                isShowingBackgroundImage
                  ? 'bg-white/90 dark:bg-[#2A1D0F]/95 hover:bg-white dark:hover:bg-[#342415]'
                  : 'bg-white/90 dark:bg-[#2A1D0F]/95 hover:bg-white dark:hover:bg-[#342415]'
              }`}
              aria-label={intl.formatMessage({
                id: 'marketHeader.toggle',
                defaultMessage: 'Toggle market header',
              })}
            >
              {/* Show chevron: down when expanded (content visible), up when collapsed (content hidden) */}
              {/* When null: collapsed on md/sm (up), expanded on lg+ (down) */}
              {isCollapsed === null ? (
                <>
                  {/* Mobile/Tablet: collapsed by default, show up chevron */}
                  <CaretUp size={20} className="sm:w-5 sm:h-5 md:w-5 md:h-5 lg:hidden text-[#3D3529] dark:text-[#C4A882] group-hover:text-[#1E1A14] dark:group-hover:text-[#F2EADF]" />
                  {/* Desktop: expanded by default, show down chevron */}
                  <CaretDown size={18} className="hidden lg:block text-[#3D3529] dark:text-[#C4A882] group-hover:text-[#1E1A14] dark:group-hover:text-[#F2EADF]" />
                </>
              ) : isCollapsed ? (
                <CaretUp size={20} className="sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-[18px] lg:h-[18px] text-[#3D3529] dark:text-[#C4A882] group-hover:text-[#1E1A14] dark:group-hover:text-[#F2EADF]" />
              ) : (
                <CaretDown size={20} className="sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-[18px] lg:h-[18px] text-[#3D3529] dark:text-[#C4A882] group-hover:text-[#1E1A14] dark:group-hover:text-[#F2EADF]" />
              )}
            </button>
          )}
        </div>

        {/* Collapsible Content - Always in DOM for SEO, visually hidden when collapsed */}
        {/* Default: collapsed on md and smaller, expanded on lg+ via Tailwind */}
        {/* JavaScript can override with isCollapsed state */}
        <div 
          className={`overflow-hidden ${
            isCollapsed === null 
              ? 'max-h-0 opacity-0 lg:max-h-[5000px] lg:opacity-100 pointer-events-none lg:pointer-events-auto' // SSR default: collapsed on md/sm, expanded on lg+ (no transition on initial load)
              : `transition-[max-height,opacity] duration-300 ease-in-out ${
                  isCollapsed 
                    ? 'max-h-0 opacity-0 pointer-events-none' // Manually collapsed
                    : 'max-h-[5000px] opacity-100 pointer-events-auto' // Manually expanded
                }`
          }`}
          aria-hidden={isCollapsed === null ? undefined : isCollapsed}
        >
          {/* Description - Always in DOM for SEO, visually hidden when collapsed */}
          {(description || locationHeaderText.overviewText) && (
            <div className={`text-base sm:text-lg leading-relaxed max-w-5xl lg:max-w-6xl mb-6 ${
              isShowingBackgroundImage 
                ? (useWhiteText ? 'text-white/90' : 'text-gray-700')
                : 'text-[#3D3529] dark:text-[#C4A882]'
            }`}>
              {/* Only show boilerplate description if there's no generated text */}
              {description && !locationHeaderText.overviewText && (
                <p className="mb-2">
                  {description}
                </p>
              )}
              {locationHeaderText.overviewText && (
                <p className="mb-2">
                  {locationHeaderText.overviewText}
                </p>
              )}
              {/* Q&A structure for AI Overviews - hidden but crawlable */}
              <div className="sr-only">
                <p>
                  <strong>
                    <FormattedMessage
                      id="marketHeader.qa.remoteQuestion"
                      defaultMessage="Is {location} remote-friendly?"
                      values={{ location: translatedLocation }}
                      description="Question in Q&A section about remote work availability in a location (hidden, for SEO)"
                    />
                  </strong>{' '}
                  <FormattedMessage
                    id="marketHeader.qa.remoteAnswer"
                    defaultMessage="Yes, {answer}"
                    values={{ answer: qaContent.remote }}
                    description="Answer in Q&A section about remote work availability (hidden, for SEO)"
                  />
                </p>
                <p>
                  <strong>
                    <FormattedMessage
                      id="marketHeader.qa.salaryQuestion"
                      defaultMessage="What is the average salary in {location}?"
                      values={{ location: translatedLocation }}
                      description="Question in Q&A section about average salary in a location (hidden, for SEO)"
                    />
                  </strong>{' '}
                  {qaContent.salary}
                </p>
                <p>
                  <strong>
                    <FormattedMessage
                      id="marketHeader.qa.languageQuestion"
                      defaultMessage="What languages are required in {location}?"
                      values={{ location: translatedLocation }}
                      description="Question in Q&A section about language requirements in a location (hidden, for SEO)"
                    />
                  </strong>{' '}
                  {qaContent.language}
                </p>
              </div>
            </div>
          )}
          {/* Insight Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Salary Card */}
          <div
            className={`rounded-[2.5rem] p-6 ${
              isShowingBackgroundImage
                ? 'bg-white/90 dark:bg-[#1F1509]/92 border border-white/40 dark:border-[#4A3520]/60'
                : 'bg-white/85 dark:bg-[#1F1509]/95 shadow-lg dark:shadow-none border border-[#DDD5C8]/70 dark:border-[#4A3520]/85'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#F5EDD0] dark:bg-[#2A1E08] flex items-center justify-center">
                <Wallet size={20} className="text-[#6E4F00] dark:text-[#D4A820]" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#3D3529] dark:text-[#C4A882]">
                  <FormattedMessage
                    id="marketHeader.salary.label"
                    defaultMessage="Salary Trends"
                    description="Label for salary trends card in market header"
                  />
                </div>
                {salaryValue && (
                  <div className="text-2xl font-bold text-[#1E1A14] dark:text-[#F2EADF]">
                    {salaryValue}
                  </div>
                )}
              </div>
            </div>
            {salaryRange && (
              <div className="mt-4">
                <div className="text-xs text-[#7A6E5F] dark:text-[#B8A48C] mb-1">
                  {salaryRange.isPercentile ? (
                    <FormattedMessage
                      id="marketHeader.salary.rangePercentile"
                      defaultMessage="25th-75th percentile range"
                      description="Label for salary range showing 25th to 75th percentile"
                    />
                  ) : (
                    <FormattedMessage
                      id="marketHeader.salary.range"
                      defaultMessage="Salary range"
                      description="Label for salary range (min-max)"
                    />
                  )}
                </div>
                <div className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                  €{Math.round(salaryRange.low / 1000)}k - €{Math.round(salaryRange.high / 1000)}k/mo
                </div>
                {finlandAverages?.avgSalaryNum && finlandAverages.avgSalaryNum > 0 && (
                  <div className="mt-2 text-xs text-[#7A6E5F] dark:text-[#B8A48C]">
                    <FormattedMessage
                      id="marketHeader.salary.finlandComparison"
                      defaultMessage="Finland avg: {finlandAvg}"
                      values={{ finlandAvg: finlandAverages.avgSalaryFormatted || `€${Math.round(finlandAverages.avgSalaryNum / 1000)}k` }}
                      description="Text showing Finland average salary for comparison with location-specific salary"
                    />
                    {avgSalaryNum > 0 && (
                      <span className={`ml-0.5 ${avgSalaryNum > finlandAverages.avgSalaryNum ? 'text-[#3B5E45] dark:text-[#98CFA8]' : avgSalaryNum < finlandAverages.avgSalaryNum ? 'text-[#6E4F00] dark:text-[#D4A820]' : ''}`}>
                        {avgSalaryNum > finlandAverages.avgSalaryNum ? '↑' : avgSalaryNum < finlandAverages.avgSalaryNum ? '↓' : '='}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
            {locationHeaderText.salaryBlurb && (
              <p className="mt-3 text-xs sm:text-sm leading-relaxed text-[#3D3529] dark:text-[#C4A882]">
                {locationHeaderText.salaryBlurb}
              </p>
            )}
          </div>

          {/* Work Model Card */}
          <div
            className={`rounded-[2.5rem] p-6 ${
              isShowingBackgroundImage
                ? 'bg-white/90 dark:bg-[#1F1509]/92 border border-white/40 dark:border-[#4A3520]/60'
                : 'bg-white/85 dark:bg-[#1F1509]/95 shadow-lg dark:shadow-none border border-[#DDD5C8]/70 dark:border-[#4A3520]/85'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#C8DDCE] dark:bg-[#1A2E22] flex items-center justify-center">
                <Laptop size={20} className="text-[#3B5E45] dark:text-[#98CFA8]" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#3D3529] dark:text-[#C4A882]">
                  <FormattedMessage
                    id="marketHeader.workModel.label"
                    defaultMessage="Work Model"
                    description="Section label for work model statistics (remote, hybrid, onsite) in market header"
                  />
                </div>
                {workModelData.total > 0 && workModelData.headline ? (
                  <>
                    <div className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                      <FormattedMessage
                        id={workModelData.headline}
                        defaultMessage={workModelData.headlineDefault}
                        description="Dynamic headline describing work model distribution (e.g., 'Primarily Onsite', 'Remote-Friendly')"
                      />
                    </div>
                    {workModelData.breakdown && (
                      <div className="text-xs font-normal text-[#7A6E5F] dark:text-[#B8A48C] mt-0.5">
                        {(() => {
                          const parts: string[] = [];
                          if (workModelData.breakdown && workModelData.breakdown.onsitePercent !== null) {
                            parts.push(intl.formatMessage(
                              { id: 'marketHeader.workModel.breakdown.onsite', defaultMessage: '{percent}% onsite', description: 'Text showing percentage of onsite positions in work model breakdown, where {percent} is the number' },
                              { percent: workModelData.breakdown.onsitePercent }
                            ));
                          }
                          if (workModelData.breakdown && workModelData.breakdown.hybridPercent !== null) {
                            parts.push(intl.formatMessage(
                              { id: 'marketHeader.workModel.breakdown.hybrid', defaultMessage: '{percent}% hybrid', description: 'Text showing percentage of hybrid positions in work model breakdown, where {percent} is the number' },
                              { percent: workModelData.breakdown.hybridPercent }
                            ));
                          }
                          if (workModelData.breakdown && workModelData.breakdown.remotePercent !== null) {
                            parts.push(intl.formatMessage(
                              { id: 'marketHeader.workModel.breakdown.remote', defaultMessage: '{percent}% remote', description: 'Text showing percentage of remote positions in work model breakdown, where {percent} is the number' },
                              { percent: workModelData.breakdown.remotePercent }
                            ));
                          }
                          return parts.join(' • ');
                        })()}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                    <FormattedMessage
                      id="marketHeader.workModel.noData"
                      defaultMessage="Flexible"
                      description="Fallback text shown when work model distribution data is not available, indicating flexible work options"
                    />
                  </div>
                )}
              </div>
            </div>
            {workModelData.total > 0 && (
              <figure className="mt-4">
                <WorkModelBar
                  onsite={workModelData.onsite}
                  hybrid={workModelData.hybrid}
                  remote={workModelData.remote}
                  intl={intl}
                />
                <figcaption className="sr-only">
                  {intl.formatMessage(
                    {
                      id: 'marketHeader.workModel.chartDescription',
                      defaultMessage: 'Work model distribution chart for {location} showing {onsitePercent}% onsite, {hybridPercent}% hybrid, and {remotePercent}% remote positions.',
                      description: 'Accessibility description for work model distribution chart, where {location}, {onsitePercent}, {hybridPercent}, and {remotePercent} are dynamic values'
                    },
                    {
                      location: translatedLocation,
                      onsitePercent: Math.round((workModelData.onsite / workModelData.total) * 100),
                      hybridPercent: Math.round((workModelData.hybrid / workModelData.total) * 100),
                      remotePercent: Math.round((workModelData.remote / workModelData.total) * 100),
                    }
                  )}
                </figcaption>
              </figure>
            )}
            {finlandAverages?.remotePercentage !== undefined && (
              <div className="mt-2 text-xs text-[#7A6E5F] dark:text-[#B8A48C]">
                  <FormattedMessage
                    id="marketHeader.workModel.finlandComparison"
                    defaultMessage="Finland: {finlandRemote}% remote/hybrid"
                    description="Comparison text showing Finland-wide remote/hybrid work percentage, where {finlandRemote} is the percentage"
                    values={{ finlandRemote: finlandAverages.remotePercentage }}
                  />
                {remotePercentage > 0 && (
                  <span className={`ml-0.5 ${remotePercentage > finlandAverages.remotePercentage ? 'text-[#3B5E45] dark:text-[#98CFA8]' : remotePercentage < finlandAverages.remotePercentage ? 'text-[#6E4F00] dark:text-[#D4A820]' : ''}`}>
                    {remotePercentage > finlandAverages.remotePercentage ? '↑' : remotePercentage < finlandAverages.remotePercentage ? '↓' : '='}
                  </span>
                )}
              </div>
            )}
            {locationHeaderText.workModelBlurb && (
              <p className="mt-3 text-xs sm:text-sm leading-relaxed text-[#3D3529] dark:text-[#C4A882]">
                {locationHeaderText.workModelBlurb}
              </p>
            )}
          </div>

          {/* Language Card */}
          <div
            className={`rounded-[2.5rem] p-6 ${
              isShowingBackgroundImage
                ? 'bg-white/90 dark:bg-[#1F1509]/92 border border-white/40 dark:border-[#4A3520]/60'
                : 'bg-white/85 dark:bg-[#1F1509]/95 shadow-lg dark:shadow-none border border-[#DDD5C8]/70 dark:border-[#4A3520]/85'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#DDD5C8] dark:bg-[#2A1D0F] flex items-center justify-center">
                <Translate size={20} className="text-[#5A3E20] dark:text-[#D4B898]" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#3D3529] dark:text-[#C4A882]">
                  <FormattedMessage
                    id="marketHeader.language.label"
                    defaultMessage="Language Demand"
                    description="Section label for language requirement statistics in market header"
                  />
                </div>
                {languageData.total > 0 ? (
                  <div className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                    {Math.round((languageData.english / languageData.total) * 100)}%{' '}
                    <FormattedMessage
                      id="marketHeader.language.english"
                      defaultMessage="English"
                      description="Label for English language requirement category in language distribution chart"
                    />
                  </div>
                ) : (
                  <div className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                    <FormattedMessage
                      id="marketHeader.language.mixed"
                      defaultMessage="Mixed"
                      description="Label for mixed language requirement category (both English and local language) in language distribution chart"
                    />
                  </div>
                )}
              </div>
            </div>
            {languageData.total > 0 && (
              <figure className="mt-4">
                <LanguageBar english={languageData.english} local={languageData.local} />
                <figcaption className="sr-only">
                  {intl.formatMessage(
                    {
                      id: 'marketHeader.language.chartDescription',
                      defaultMessage: 'Language requirement distribution chart for {location} showing {englishPercent}% English and {localPercent}% local language requirements.',
                      description: 'Accessibility description for language requirement distribution chart, where {location}, {englishPercent}, and {localPercent} are dynamic values'
                    },
                    {
                      location: translatedLocation,
                      englishPercent: Math.round((languageData.english / languageData.total) * 100),
                      localPercent: Math.round((languageData.local / languageData.total) * 100),
                    }
                  )}
                </figcaption>
              </figure>
            )}
            {finlandAverages?.englishLanguagePercentage !== undefined && (
              <div className="mt-2 text-xs text-[#7A6E5F] dark:text-[#B8A48C]">
                <FormattedMessage
                  id="marketHeader.language.finlandComparison"
                  defaultMessage="Finland: {finlandEnglish}% English"
                  values={{ finlandEnglish: finlandAverages.englishLanguagePercentage }}
                  description="Text showing Finland average English language percentage for comparison"
                />
                {languageData.total > 0 && (
                  <span className={`ml-0.5 ${(languageData.english / languageData.total) * 100 > finlandAverages.englishLanguagePercentage ? 'text-[#3B5E45] dark:text-[#98CFA8]' : (languageData.english / languageData.total) * 100 < finlandAverages.englishLanguagePercentage ? 'text-[#6E4F00] dark:text-[#D4A820]' : ''}`}>
                    {(languageData.english / languageData.total) * 100 > finlandAverages.englishLanguagePercentage ? '↑' : (languageData.english / languageData.total) * 100 < finlandAverages.englishLanguagePercentage ? '↓' : '='}
                  </span>
                )}
              </div>
            )}
            {locationHeaderText.languageBlurb && (
              <p className="mt-3 text-xs sm:text-sm leading-relaxed text-[#3D3529] dark:text-[#C4A882]">
                {locationHeaderText.languageBlurb}
              </p>
            )}
          </div>
        </div>

        {/* Visually Hidden Table for Google Answer Box (sr-only) */}
        <div className="sr-only">
          <table>
            <caption>
              <FormattedMessage
                id="marketHeader.salaryPercentileSeo.caption"
                defaultMessage="Advertised salary distribution in {location}, Finland — full-time roles"
                values={{ location: translatedLocation.split(',')[0]?.trim() || translatedLocation }}
                description="Hidden table caption for location salary percentile data"
              />
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  <FormattedMessage
                    id="marketHeader.salaryPercentileSeo.percentile"
                    defaultMessage="Percentile"
                    description="Hidden location salary percentile table header"
                  />
                </th>
                <th scope="col">
                  <FormattedMessage
                    id="marketHeader.salaryPercentileSeo.salary"
                    defaultMessage="Monthly salary (EUR)"
                    description="Hidden location salary percentile table header"
                  />
                </th>
              </tr>
            </thead>
            {salaryPercentileSeoData ? (
              <tbody>
                <tr>
                  <td>
                    <FormattedMessage
                      id="marketHeader.salaryPercentileSeo.p25"
                      defaultMessage="25th percentile"
                      description="Hidden location salary percentile row label"
                    />
                  </td>
                  <td>€{Math.round(salaryPercentileSeoData.percentile25).toLocaleString()}/month</td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage
                      id="marketHeader.salaryPercentileSeo.p50"
                      defaultMessage="Median (50th percentile)"
                      description="Hidden location salary percentile row label"
                    />
                  </td>
                  <td>€{Math.round(salaryPercentileSeoData.percentile50).toLocaleString()}/month</td>
                </tr>
                <tr>
                  <td>
                    <FormattedMessage
                      id="marketHeader.salaryPercentileSeo.p75"
                      defaultMessage="75th percentile"
                      description="Hidden location salary percentile row label"
                    />
                  </td>
                  <td>€{Math.round(salaryPercentileSeoData.percentile75).toLocaleString()}/month</td>
                </tr>
              </tbody>
            ) : null}
          </table>
          {salaryPercentileSeoData?.sampleSize ? (
            <p>
              <FormattedMessage
                id="marketHeader.salaryPercentileSeo.sampleSize"
                defaultMessage="Based on {count} job postings with explicit salary data in {location}, Finland."
                values={{
                  count: intl.formatNumber(salaryPercentileSeoData.sampleSize),
                  location: translatedLocation.split(',')[0]?.trim() || translatedLocation,
                }}
                description="Hidden explanatory text under location salary percentile table"
              />
            </p>
          ) : null}
          {workModelData.total > 0 ? (
            <table>
              <caption>
                <FormattedMessage
                  id="marketHeader.workModelSeo.caption"
                  defaultMessage="Work model distribution in {location}"
                  values={{ location: translatedLocation }}
                  description="Hidden table caption for work model distribution"
                />
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    <FormattedMessage
                      id="marketHeader.workModelSeo.model"
                      defaultMessage="Work model"
                      description="Hidden work model table header"
                    />
                  </th>
                  <th scope="col">
                    <FormattedMessage
                      id="marketHeader.workModelSeo.share"
                      defaultMessage="Share of listings"
                      description="Hidden work model table header"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Onsite</td>
                  <td>{Math.round((workModelData.onsite / workModelData.total) * 100)}%</td>
                </tr>
                <tr>
                  <td>Hybrid</td>
                  <td>{Math.round((workModelData.hybrid / workModelData.total) * 100)}%</td>
                </tr>
                <tr>
                  <td>Remote</td>
                  <td>{Math.round((workModelData.remote / workModelData.total) * 100)}%</td>
                </tr>
              </tbody>
            </table>
          ) : null}
          {languageData.total > 0 ? (
            <table>
              <caption>
                <FormattedMessage
                  id="marketHeader.languageSeo.caption"
                  defaultMessage="Language demand in {location}"
                  values={{ location: translatedLocation }}
                  description="Hidden table caption for language demand distribution"
                />
              </caption>
              <thead>
                <tr>
                  <th scope="col">
                    <FormattedMessage
                      id="marketHeader.languageSeo.language"
                      defaultMessage="Language category"
                      description="Hidden language demand table header"
                    />
                  </th>
                  <th scope="col">
                    <FormattedMessage
                      id="marketHeader.languageSeo.share"
                      defaultMessage="Share of listings"
                      description="Hidden language demand table header"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>English</td>
                  <td>{Math.round((languageData.english / languageData.total) * 100)}%</td>
                </tr>
                <tr>
                  <td>Local languages</td>
                  <td>{Math.round((languageData.local / languageData.total) * 100)}%</td>
                </tr>
              </tbody>
            </table>
          ) : null}
        </div>


        {/* Filter Pills and Metadata */}
        <aside className="flex flex-wrap items-center gap-2 justify-between" aria-label={intl.formatMessage({ id: 'marketHeader.filterNavigation', defaultMessage: 'Location filters', description: 'Accessibility label for location filter navigation section in market header' })}>
          <Tooltip
            content={intl.formatMessage({ id: 'tooltip.filter.english', defaultMessage: 'Filter to show only jobs that require English language', description: 'Tooltip text for English language filter button' })}
            position="top"
          >
            <button
              onClick={() => {
                const nextState = buildFilterState('requiredLanguages', 'English');
                pushIndexUiState(nextState);
              }}
              className={`hidden lg:flex px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors transition-transform duration-200 hover:scale-105 items-center gap-1.5 cursor-pointer border-2 ${
                hasEnglishFilter
                  ? `${FILTER_CAT_JOB} shadow-sm dark:shadow-none`
                  : isShowingBackgroundImage
                  ? 'bg-white/90 dark:bg-[#2A1D0F]/95 text-[#3D3529] dark:text-[#C4A882] hover:bg-white/95 dark:hover:bg-[#342415]'
                  : 'bg-white/95 dark:bg-[#2A1D0F]/95 text-[#3D3529] dark:text-[#C4A882] hover:bg-white dark:hover:bg-[#342415]'
              }`}
            >
              <Funnel size={14} className="flex-shrink-0" />
              <span>
                <FormattedMessage
                  id="marketHeader.filter.english"
                  defaultMessage="English Only"
                  description="Filter button label to show only jobs requiring English language"
                />
              </span>
            </button>
          </Tooltip>
          <Tooltip
            content={intl.formatMessage({ id: 'tooltip.filter.remote', defaultMessage: 'Filter to show only remote or hybrid work opportunities', description: 'Tooltip text for remote/hybrid work filter button' })}
            position="top"
          >
            <button
              onClick={() => {
                const nextState = buildFilterState('remoteness', 'Remote');
                pushIndexUiState(nextState);
              }}
              className={`hidden lg:flex px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors transition-transform duration-200 hover:scale-105 items-center gap-1.5 cursor-pointer border-2 ${
                hasRemoteFilter
                  ? `${FILTER_CAT_LOCATION} shadow-sm dark:shadow-none`
                  : isShowingBackgroundImage
                  ? 'bg-white/90 dark:bg-[#2A1D0F]/95 text-[#3D3529] dark:text-[#C4A882] hover:bg-white/95 dark:hover:bg-[#342415]'
                  : 'bg-white/95 dark:bg-[#2A1D0F]/95 text-[#3D3529] dark:text-[#C4A882] hover:bg-white dark:hover:bg-[#342415]'
              }`}
            >
              <Funnel size={14} className="flex-shrink-0" />
              <span>
                <FormattedMessage
                  id="marketHeader.filter.remote"
                  defaultMessage="Remote Jobs"
                  description="Filter button label to show only remote or hybrid jobs"
                />
              </span>
            </button>
          </Tooltip>
          <Tooltip
            content={intl.formatMessage({ id: 'tooltip.filter.highSalary', defaultMessage: 'Filter to show only jobs with high salary (€5000+/month)', description: 'Tooltip text for high salary filter button' })}
            position="top"
          >
            <button
              onClick={() => {
                // Set a high salary threshold (e.g., 5000 EUR/month)
                const nextState = buildFilterState('salaryRangeMin', '5000');
                pushIndexUiState(nextState);
              }}
              className={`hidden lg:flex px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors transition-transform duration-200 hover:scale-105 items-center gap-1.5 cursor-pointer border-2 ${
                hasHighSalaryFilter
                  ? `${FILTER_CAT_SALARY} shadow-sm dark:shadow-none`
                  : isShowingBackgroundImage
                  ? 'bg-white/90 dark:bg-[#2A1D0F]/95 text-[#3D3529] dark:text-[#C4A882] hover:bg-white/95 dark:hover:bg-[#342415]'
                  : 'bg-white/95 dark:bg-[#2A1D0F]/95 text-[#3D3529] dark:text-[#C4A882] hover:bg-white dark:hover:bg-[#342415]'
              }`}
            >
              <Funnel size={14} className="flex-shrink-0" />
              <span>
                <FormattedMessage
                  id="marketHeader.filter.highSalary"
                  defaultMessage="High Salary"
                  description="Filter button label to show only jobs with high salary (€5000+/month)"
                />
              </span>
            </button>
          </Tooltip>

          {/* Freshness & Authority Signals - Aligned to Right */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs text-[#3D3529] dark:text-[#C4A882] ml-auto ${
            isShowingBackgroundImage
              ? 'bg-white/90 dark:bg-[#2A1D0F]/95'
              : 'bg-white/90 dark:bg-[#2A1D0F]/95 border border-[#DDD5C8]/70 dark:border-[#4A3520]/80 shadow-sm dark:shadow-none'
          }`}>
            {lastUpdatedDate && (
              <>
                <span>
                  <FormattedMessage id="marketHeader.updated" defaultMessage="Updated:" description="Label for the last updated date of market data" />
                </span>
                <time dateTime={lastUpdatedDate.toISOString()}>
                  {intl.formatDate(lastUpdatedDate, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </time>
              </>
            )}
            {/* Samples button - renders server-side when sampleCount > 0 */}
            {sampleCount > 0 && (
              <>
                {lastUpdatedDate && <span className="text-[#7A6E5F] dark:text-[#8A7060]">•</span>}
                <Link
                  href="/locations"
                  className="text-[#3B5E45] dark:text-[#98CFA8] hover:text-[#2A4533] dark:hover:text-[#B6D4BC] transition-colors cursor-pointer"
                  title={intl.formatMessage({
                    id: 'marketHeader.viewLocationHub',
                    defaultMessage: 'View location hub',
                    description: 'Link label to navigate to location hub page from market header'
                  })}
                >
                  <FormattedMessage
                    id="marketHeader.samples"
                    defaultMessage="{count} samples"
                    values={{ count: intl.formatNumber(sampleCount) }}
                    description="Link text showing the number of job samples used for market statistics, linking to location hub"
                  />
                </Link>
              </>
            )}
          </div>
        </aside>
        </div>

      </div>
    </section>
    </>
  );
};
